import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { NotesService } from '../../services/notes.service';
import {
  NoteViewModel,
  NoteAttachment,
  CreateNoteViewModel,
  UpdateNoteViewModel,
  NOTE_BG_COLORS,
} from '../../models/note.model';

interface AttachmentPreviewItem {
  name: string;
  type: string;
  size: string;
  isExisting: boolean;
  attachmentId?: string;
  fileRef?: File;
}

@Component({
  selector: 'app-note-editor',
  templateUrl: './note-editor.component.html',
  styleUrls: ['./note-editor.component.scss'],
})
export class NoteEditorComponent implements OnInit, OnChanges {
  @Input() note: NoteViewModel | null = null; // null = create mode
  @Input() TransactionType: string;
  @Input() TransactionId: string;
  @Output() onSaved = new EventEmitter<NoteViewModel>();
  @Output() onCancel = new EventEmitter<void>();

  @ViewChild('contentArea') contentArea: ElementRef;
  @ViewChild('tagInput') tagInput: ElementRef;
  @ViewChild('mentionInput') mentionInput: ElementRef;
  @ViewChild('titleInput') titleInput: ElementRef;

  // Form state
  title: string = '';
  noteContent: string = '';
  noteType: string = 'Internal';
  isPinned: boolean = false;
  isArchived: boolean = false;
  backgroundColor: string = '#ffffff';
  isCheckList: boolean = false;
  hasReminder: boolean = false;
  reminderDate: string = '';
  displayOrder: number = null;
  tags: string[] = [];
  mentionedUsers: string[] = [];

  // UI state
  newTagInput: string = '';
  showColorPicker: boolean = false;
  showTagInput: boolean = false;
  showReminderPicker: boolean = false;
  isLoading: boolean = false;
  isSaving: boolean = false;

  attachmentPreviews: AttachmentPreviewItem[] = [];
  attachmentsToDelete: string[] = [];

  todayString: string = new Date().toISOString().slice(0, 16);
  
  bgColors = NOTE_BG_COLORS;
  noteTypeOptions = ['Internal', 'External'];

  get isEditMode(): boolean {
    return !!this.note;
  }

  get isFormValid(): boolean {
    return this.noteContent && this.noteContent.trim().length > 0;
  }

  private get newFiles(): File[] {
    return this.attachmentPreviews
      .filter (p => !p.isExisting && p.fileRef)
      .map(p => p.fileRef);
  }

  constructor(
    private notesService: NotesService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.populateForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.note) {
      this.populateForm();
    }
  }

  populateForm(): void {
    if (this.note) {
      this.title = this.note.Title || '';
      this.noteContent = this.note.NoteContent || '';
      this.noteType = this.note.NoteType || 'Internal';
      this.isPinned = this.note.IsPinned;
      this.isArchived = this.note.IsArchived;
      this.backgroundColor = this.note.BackgroundColor || '#ffffff';
      this.isCheckList = this.note.IsCheckList;
      this.hasReminder = this.note.HasReminder;
      this.reminderDate = this.note.ReminderDate
        ? this.note.ReminderDate.substring(0, 16)
        : '';
      this.tags = this.notesService.parseTags(this.note.Tags);
      this.mentionedUsers =
        this.note.Mentions
          ? this.note.Mentions.map((m) => m.MentionedUserId)
          : [];
      
      this.attachmentPreviews = [];
      this.attachmentsToDelete = [];
      this.focusTitleInput();
      
      if (this.note.AttachmentsJson) {
        try {
          const parsed: NoteAttachment[] = JSON.parse(this.note.AttachmentsJson);
          this.attachmentPreviews = parsed.map(att => ({
            name: att.FileURL ? att.FileURL.split('/').pop() || `Attachment` : `Attachment`,
            type: att.FileType || 'application/octet-stream',
            size: '',
            isExisting: true,
            attachmentId: att.AttachmentId,
          }));
        } catch {

        }
      }
    } else {
      this.resetForm();
    }
  }

  resetForm(): void {
    this.title = '';
    this.noteContent = '';
    this.noteType = 'Internal';
    this.isPinned = false;
    this.isArchived = false;
    this.backgroundColor = '#ffffff';
    this.isCheckList = false;
    this.hasReminder = false;
    this.reminderDate = this.todayString;
    this.tags = [];
    this.mentionedUsers = [];
    //this.attachmentFiles = [];
    this.attachmentPreviews = [];
    this.newTagInput = '';
    this.showColorPicker = false;
    this.showTagInput = false;
    this.showReminderPicker = false;
    this.focusTitleInput();
  }

  // ─── Tags 

  addTag(): void {
    const tag = this.newTagInput.trim();
    if (tag && !this.tags.includes(tag) && this.tags.length < 10) {
      this.tags.push(tag);
      this.newTagInput = '';
    }
  }

  removeTag(tag: string): void {
    this.tags = this.tags.filter((t) => t !== tag);
  }

  onTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addTag();
    }
  }

  // ─── Attachments 

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    Array.from(input.files).forEach((file) => {
      const maxSizeMB = 10;
      if (file.size / 1024 / 1024 > maxSizeMB) {
        this.toastr.error(`File ${file.name} exceeds ${maxSizeMB}MB limit.`, 'Error');
        return;
      }
      //this.attachmentFiles.push(file);
      this.attachmentPreviews.push({
        name: file.name,
        type: file.type,
        size: (file.size / 1024).toFixed(1) + ' KB',
        isExisting: false,
        fileRef: file,
      });
    });
    input.value = '';
  }

  removeAttachment(index: number): void {
    const item = this.attachmentPreviews[index];
    if (!item) return;

    if(item.isExisting && item.attachmentId) {
      this.attachmentsToDelete.push(item.attachmentId);
    }

    this.attachmentPreviews.splice(index, 1);
  }

  getFileIcon(type: string): string {
    if (type.includes('image')) return '🖼️';
    if (type.includes('pdf')) return '📄';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('word')) return '📝';
    return '📎';
  }

  // ─── Save 

  save(): void {
    if (!this.isFormValid) {
      this.toastr.warning('Note content cannot be empty.', 'Validation');
      return;
    }

    this.isSaving = true;

    if (this.isEditMode) {
      this.updateNote();
    } else {
      this.createNote();
    }
  }

  private createNote(): void {
    const model: CreateNoteViewModel = {
      Title: this.title,
      NoteContent: this.noteContent,
      TransactionType: this.TransactionType,
      TransactionId: this.TransactionId,
      CompanyId: JSON.parse(localStorage.getItem("ChnageCompnayId")).Id,
      NoteType: this.noteType,
      IsPinned: this.isPinned,
      IsArchived: this.isArchived,
      BackgroundColor: this.backgroundColor,
      IsCheckList: this.isCheckList,
      HasReminder: this.hasReminder,
      ReminderDate: this.hasReminder && this.reminderDate ? this.reminderDate : null,
      DisplayOrder: this.displayOrder,
      Tags: this.tags,
      MentionedUsers: this.mentionedUsers,
    };

    this.notesService.createNote(model).subscribe(
      (result) => {
        this.uploadAttachmentsIfAny(result.NoteId, result);
      },
      (err) => {
        this.isSaving = false;
        this.toastr.error('Failed to create note.', 'Error');
      }
    );
  }

  getTodayString(): string{
    const today = new Date();
    return today.toISOString().slice(0, 16);
  }

  private updateNote(): void {
    const model: UpdateNoteViewModel = {
      Title: this.title,
      NoteContent: this.noteContent,
      TransactionType: this.TransactionType,
      TransactionId: this.TransactionId,
      CompanyId: JSON.parse(localStorage.getItem("ChnageCompnayId")).Id,
      NoteType: this.noteType,
      IsPinned: this.isPinned,
      IsArchived: this.isArchived,
      BackgroundColor: this.backgroundColor,
      IsCheckList: this.isCheckList,
      HasReminder: this.hasReminder,
      ReminderDate: this.hasReminder && this.reminderDate ? this.reminderDate : null,
      DisplayOrder: this.displayOrder,
      Tags: this.tags,
    };

    this.notesService.updateNote(this.note.NoteId, model).subscribe(
      (result) => {
        this.deleteRemovedAttachments(result.NoteId, () => {
          this.uploadAttachmentsIfAny(result.NoteId, result);
        });
      },
      (err) => {
        this.isSaving = false;
        this.toastr.error('Failed to update note.', 'Error');
      }
    );
  }

  private deleteRemovedAttachments(noteId: string, callback: () => void): void {
    if (this.attachmentsToDelete.length === 0) {
      callback();
      return;
    }

    let deleted = 0;
    this.attachmentsToDelete.forEach((attachmentId) => {
      this.notesService.deleteAttachment(attachmentId).subscribe(
        () => {
          deleted++;
          if (deleted === this.attachmentsToDelete.length) {
            callback();
          }
        },
        () => {
          deleted++;
          if (deleted === this.attachmentsToDelete.length) {
            callback();
          }
        }
      );
    });
  }

  private uploadAttachmentsIfAny(noteId: string, savedNote: NoteViewModel): void {
    const files = this.newFiles;

  if (files.length === 0) {
    this.finishSave(savedNote);
    return;
  }

  let done = 0;
  files.forEach(file => {
    this.notesService
      .addAttachment({ NoteId: noteId, FileURL: '', FileType: file.type }, file)
      .subscribe(
        () => { if (++done === files.length) this.finishSave(savedNote); },
        () => {
          this.toastr.warning(`Could not upload ${file.name}.`, 'Warning');
          if (++done === files.length) this.finishSave(savedNote);
        }
      );
  });
}

private finishSave(savedNote: NoteViewModel): void {
  this.isSaving = false;
  this.toastr.success(
    this.isEditMode ? 'Note updated successfully.' : 'Note created successfully.',
    'Success'
  );
  this.onSaved.emit(savedNote);
}

  private focusTitleInput(): void {
    setTimeout(() => {
      if (this.titleInput && this.titleInput.nativeElement) {
        this.titleInput.nativeElement.focus();
        this.titleInput.nativeElement.select();
      }
    });
  }
  cancel(): void {
    this.onCancel.emit();
  }

  
}